"use client";
import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Box,
  Button,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { FaCheckCircle } from "react-icons/fa";
import theme from "../../../theme";
import { useSnackbar } from "../../../context/SnackbarContext";
import api from "../../../api/axiosInstance";

interface ApprovalFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (amount: string) => void;
  applicationId: number;
}

export default function ApprovalFeedbackModal({
  open,
  onClose,
  onSubmit,
  applicationId,
}: ApprovalFeedbackModalProps) {
  const { showSnackbar } = useSnackbar();
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateAmount = (value: string) => {
    if (!value.trim()) {
      setError("Amount is required");
      return false;
    }

    const numValue = Number(value);
    if (isNaN(numValue) || numValue <= 0) {
      setError("Please enter a valid positive amount");
      return false;
    }

    setError("");
    return true;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmount(value);
    validateAmount(value);
  };

  const handleSubmit = async () => {
    if (!validateAmount(amount)) {
      showSnackbar("Please enter a valid amount", "warning");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        scholarship_application_id: applicationId,
        is_approved: true,
        amount_granted: Number(amount),
      };

      await api.post(
        "/scholarship-applications/final-admin-approval/",
        payload
      );

      showSnackbar("Application approved successfully", "success");

      // Reset form and close modal after successful submission
      setAmount("");
      setError("");
      setTimeout(() => {
        onSubmit(amount);
      }, 1000);
    } catch (error: any) {
      console.error("Error approving application:", error);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.detail || 
                          "Failed to approve application. Please try again.";
      
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setAmount("");
      setError("");
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxWidth: "500px",
            width: "100%",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            bgcolor: "#2e7d32", // Green color for approval
            color: "white",
            display: "flex",
            alignItems: "center",
          }}
        >
          <FaCheckCircle style={{ marginRight: "10px" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Approval Details
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 2 }}>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please specify the amount to be granted for this scholarship
              application. This information will be recorded and shared with the
              applicant.
            </Typography>

            <TextField
              label="Amount Granted"
              placeholder="Enter the scholarship amount to be granted..."
              value={amount}
              onChange={handleAmountChange}
              fullWidth
              variant="outlined"
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">PKR</InputAdornment>
                ),
                sx: {
                  borderRadius: "6px",
                  fontSize: "14px",
                },
              }}
              required
              error={!!error}
              helperText={error}
              sx={{ mb: 2 }}
              disabled={loading}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
                sx={{
                  mr: 2,
                  color: theme.palette.colors["white"]["400"],
                  backgroundColor: theme.palette.colors.black["600"],
                  borderColor: "#5a5a5a",
                  "&:hover": {
                    borderColor: "#3a3a3a",
                    backgroundColor: theme.palette.colors.gray["800"],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!amount.trim() || !!error || loading}
                sx={{
                  color: "white",
                  bgcolor: "#2e7d32", // Green color for approval
                  "&:hover": {
                    bgcolor: "#1b5e20", // Darker green on hover
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={16}
                      style={{
                        marginRight: 8,
                        color: "white",
                      }}
                    />
                    Approving...
                  </>
                ) : (
                  "Confirm Approval"
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

    </>
  );
}
